import { formula } from '@flowus/formula';
import type { CollectionSchema, SegmentDTO } from '@next-space/fe-api-idl';
import {
  AggregationAction,
  BlockStatus,
  CollectionSchemaType,
  TextType,
} from '@next-space/fe-api-idl';
import dayjs from 'dayjs';
import type { getRollupValue as getRollupValue0 } from 'src/bitable/table-view/cell/rollup/get-rollup-value';
import {
  isDateAggregation,
  isPercentAggregation,
  isShowOriginalValue,
} from 'src/bitable/table-view/footer/helper';
import { checkPropRecur } from 'src/bitable/v2';
import type { BiSchemaResolver } from 'src/bitable/v2/utils/resolvers';
import { DATE_TIME_FORMAT } from 'src/common/const';
import { segmentsToText } from 'src/editor/utils/editor';
import { buildDateSegment, readDateFromDateSegment } from 'src/editor/utils/segments';
import { cache, getState as getAppState, getState } from 'src/redux/store';
import { formatFloat, percentToNumber } from 'src/utils/number';
import { getUserName } from '../user/use-remark-name';
import { formatCheckBoxValue } from '@flowus/common/block/checkbox-value';
import type { RootState } from 'src/redux/types';
import { generateFormatByDate } from '@flowus/common';
import { isBuildIn } from '@flowus/common/build-in';
import { FormulaValue } from 'src/bitable/table-view/cell/formula/v2/formula-value';
import { textToSegments } from '@flowus/common/subscribe/util';
import { tryNewFormula, isNewFormulaInTime } from 'src/bitable/table-view/cell/formula/v2/utils';
import appI18n from '@flowus/common/i18n';

export let _getRollupValue: typeof getRollupValue0;

export const antiCycleSet_getRollupValue_0 = (getRollupValue: typeof _getRollupValue) => {
  _getRollupValue = getRollupValue;
};

const getRollupValue: typeof _getRollupValue = (...args) => _getRollupValue(...args);

export interface FormulaError {
  type: 'error';
  pos: {
    pmin: number;
    pmax: number;
  };
  message: string;
}

export interface FormulaPreview {
  type: 'preview';
  preview: string | React.ReactNode;
}

export interface FormulaTool {
  fromServer(formula: formula.ServerFormula): string;
  toServer(formula: string): formula.ServerFormula;
  getAvailablePropIds(propId: string): Set<string>;
  getType(propId: string): formula.ValueType | null;
  getTypeAsCollectionSchemaType(propId: string): CollectionSchemaType;
  getBoxedValue(recordId: string, propId: string): formula.Value;
  getRelationSchema(): Record<string, CollectionSchema>;
  getValue(
    recordId: string,
    propId: string
  ):
    | string
    | string[]
    | number
    | boolean
    | Date
    | formula.ListValue
    | formula.Select
    | formula.DateRangeValue
    | formula.User
    | formula.RelationPage
    | formula.Text
    | null;
  getValueAsSegments(recordId: string, propId: string): SegmentDTO[] | undefined;
  getValueAsString(recordId: string, propId: string): string | undefined;
  render(recordId: string, propId: string): string;
  validate(
    formula: string,
    collectionId: string,
    recordId: string,
    propId: string
  ): FormulaError | FormulaPreview;
}

/**
 * 翻译公式引擎返回的错误消息
 */
const translateFormulaError = (errorKey: string): string => {
  // 检查是否是带参数的 JSON 字符串
  if (errorKey.startsWith('{')) {
    try {
      const parsed = JSON.parse(errorKey);
      const key = parsed.key as string;
      const params = parsed.params as Record<string, unknown>;

      // 处理 types 参数 - 需要翻译类型键并用"或"连接
      if (params.types && Array.isArray(params.types)) {
        const translatedTypes = params.types.map((typeKey: string) => {
          const i18nKey = `common.formula_engine.${typeKey.replace('formula.', '')}`;
          return appI18n.t(i18nKey) as string;
        });
        params.types = translatedTypes.join(appI18n.t('common.formula_engine.errors.or') as string);
      }

      // 翻译主键
      const i18nKey = `common.formula_engine.${key.replace('formula.', '')}`;
      return appI18n.t(i18nKey, params) as string;
    } catch {
      return errorKey;
    }
  }

  // 简单键
  const i18nKey = `common.formula_engine.${errorKey.replace('formula.', '')}`;
  return appI18n.t(i18nKey) as string;
};

class MyEvalContext extends formula.EvalContext {
  constructor(private getState = getAppState, private collectionId: string) {
    super();
  }
  readPropFromName(recordId: string, propName: string) {
    const state = this.getState();
    const { blocks } = state;
    const record = blocks[recordId];
    if (record == null) return null;

    const collectionId = record.parentId;
    const collection = blocks[collectionId];
    if (collection == null) return null;
    if (!collection.data.schema) return null;
    const entry = Object.entries(collection.data.schema).find(([_, schema]) => {
      if (schema.name === propName) return true;
      return false;
    });
    if (!entry) return null;
    const propId = entry[0];
    return this.readProp(recordId, propId);
  }

  readProp(recordId: string, propId: string): formula.Value | null {
    const state = this.getState();
    const { blocks } = state;
    const record = blocks[recordId];
    if (record == null) return null;

    const collectionId = record.parentId;
    const collection = blocks[collectionId];
    if (collection == null) return null;

    const propSchema = collection.data.schema?.[propId];
    if (propSchema == null) return null;

    if (propSchema.type === CollectionSchemaType.FORMULA) {
      const formulaTool = getFormulaTool(collectionId);
      if (this.collectionId === collectionId) {
        return formulaTool.getBoxedValue(recordId, propId);
      }
      // 如果是访问其他多维表的公式属性，会默认返回字符串类型，这里只能自己拼接了(公式这块代码太繁琐了，不太符合2.0的要求，重构容易出问题且需要大量时间，不重构只能凑合改改，凑合用吧)
      const ret = formulaTool.getValueAsString(recordId, propId);
      return formula.ValueTool.string(ret ?? '');
    }

    if (propSchema.type === CollectionSchemaType.ROLLUP) {
      const {
        dateStartTimestamp,
        dateEndTimestamp,
        aggregationValue,
        originValues,
        originValueToStringFunc,
      } = getRollupValue(recordId, propId) ?? {};
      if (isShowOriginalValue(propSchema.aggregation)) {
        return formula.ValueTool.string(
          originValues?.map((it) => originValueToStringFunc?.(it)).join(', ') ?? ''
        );
      } else if (isDateAggregation(propSchema.aggregation)) {
        if (propSchema.aggregation === AggregationAction.DATE_RANGE) {
          return dateStartTimestamp == null
            ? null
            : formula.ValueTool.string(
                `${dayjs(dateStartTimestamp).format(DATE_TIME_FORMAT)} - ${dayjs(
                  dateEndTimestamp
                ).format(DATE_TIME_FORMAT)}`
              );
        }
        const date = dayjs(dateStartTimestamp);
        return dateStartTimestamp !== undefined && date.isValid()
          ? formula.ValueTool.date(date.toDate())
          : null;
      } else if (isPercentAggregation(propSchema.aggregation)) {
        if (typeof aggregationValue === 'string') {
          return formula.ValueTool.number(percentToNumber(aggregationValue));
        }
        return null;
      }
      return formula.ValueTool.number(Number(aggregationValue));
    }

    if (propSchema.type === CollectionSchemaType.CREATED_AT) {
      return record.createdAt == null ? null : formula.ValueTool.date(new Date(record.createdAt));
    }

    if (propSchema.type === CollectionSchemaType.UPDATED_AT) {
      return record.updatedAt == null ? null : formula.ValueTool.date(new Date(record.updatedAt));
    }

    if (propSchema.type === CollectionSchemaType.CREATED_BY) {
      const text = record.createdBy == null ? '' : getUserName(record.createdBy);
      return formula.ValueTool.string(text);
    }

    if (propSchema.type === CollectionSchemaType.UPDATED_BY) {
      const text = record.updatedBy == null ? '' : getUserName(record.updatedBy);
      return formula.ValueTool.string(text);
    }

    const segments =
      propSchema.type === CollectionSchemaType.TITLE
        ? record.data.segments
        : record.data.collectionProperties?.[propId];

    if (propSchema.type === CollectionSchemaType.CHECKBOX) {
      return formula.ValueTool.boolean(formatCheckBoxValue(segmentsToText(segments)));
    }

    if (propSchema.type === CollectionSchemaType.NUMBER) {
      const text = segmentsToText(segments);
      let num = parseFloat(text);
      if (Number.isNaN(num)) {
        num = parseFloat(text.match(/[+-]?\d+(?:\.?\d+)?/)?.[0] ?? '');
      }

      if (Number.isNaN(num)) return formula.ValueTool.number(0);
      return formula.ValueTool.number(num);
    }

    if (propSchema.type === CollectionSchemaType.DATE) {
      if (segments == null) return null;
      const segment = segments.find(
        (it) => it.type === TextType.DATE || it.type === TextType.DATETIME
      );
      if (segment == null) return null;
      const date = readDateFromDateSegment(segment);
      const endDate = segment.endDate ? readDateFromDateSegment(segment, true) : null;
      if (!dayjs(date).isValid()) return null;
      if (endDate != null && dayjs(endDate).isValid()) {
        return formula.ValueTool.dateRange(date, endDate);
      }
      return formula.ValueTool.date(date);
    }

    if (propSchema.type === CollectionSchemaType.PERSON) {
      const elements = (segments ?? []).filter((it) => it.type === TextType.USER);
      const users = elements
        .filter((v) => {
          const user = state.users[v.uuid ?? ''];
          return !!user;
        })
        .map((v) => {
          const user = state.users[v.uuid ?? '']!;
          const userName = getUserName(user.uuid);
          return formula.ValueTool.elementUser(user.uuid, userName, user.email);
        });
      return formula.ValueTool.list(formula.ValueTool.userType, users);
    }

    if (propSchema.type === CollectionSchemaType.FILE) {
      // const files = (segments ?? [])
      //   .filter((it) => it.type === TextType.URL && it.fileStorageType === 'internal')
      //   .map((it) => formula.ValueTool.elementFile(it.text, it.url ?? ''));
      // return formula.ValueTool.list(formula.ValueTool.fileType, files);
      const fileNames = (segments ?? [])
        .filter((it) => it.type === TextType.URL && it.fileStorageType === 'internal')
        .map((v) => {
          return v.text;
        })
        .join(', ');
      return formula.ValueTool.string(fileNames);
    }

    if (propSchema.type === CollectionSchemaType.MULTI_SELECT) {
      const text = segmentsToText(segments);
      const selects = text.split(',');
      const formulaSelects: string[] = [];
      propSchema.options?.forEach((v) => {
        if (selects.includes(v.value)) {
          formulaSelects.push(v.value);
        }
      });
      return formula.ValueTool.list(
        formula.ValueTool.textType,
        formulaSelects.map((v) => formula.ValueTool.elementText(v))
      );
      // const text = segmentsToText(segments);
      // const selects = text.split(',').join(', ');
      // return formula.ValueTool.string(selects);
    }
    if (propSchema.type === CollectionSchemaType.SELECT) {
      let text = '';
      let color = '';
      if (segments && segments.length > 0) {
        text = segmentsToText([segments[0]!]);
      }
      const option = propSchema.options?.filter((v) => v.value === text)[0];
      if (option) {
        color = option.color;
      }
      return formula.ValueTool.string(text);
    }
    if (propSchema.type === CollectionSchemaType.RELATION) {
      const relationPages: formula.RelationPage[] = [];
      segments?.forEach((v) => {
        if (v.type === TextType.LINK_PAGE) {
          const name = segmentsToText([v], 0, false);
          const collectionId = propSchema.collectionId ?? '';
          const recordId = v.uuid ?? '';
          const isNormal = cache.blocks[recordId]?.status === BlockStatus.NORMAL;
          isNormal &&
            relationPages.push(formula.ValueTool.elementRecord(name, collectionId, recordId));
        }
      });
      return formula.ValueTool.list(formula.ValueTool.relationPage, relationPages);
    }

    return formula.ValueTool.string(segmentsToText(segments));
  }
}

const createFormulaTool = (
  collId: string,
  tableSchema0: Record<string, CollectionSchema>,
  resolver: BiSchemaResolver,
  evalCtx: MyEvalContext
) => {
  const tableSchema = tableSchema0 ?? {};
  const relationSchema: Record<string, CollectionSchema> = {};
  const noTypeSchema = new formula.Schema();
  const relationPageNoTypeSchema = new formula.Schema();
  for (const [propId, propSchema] of Object.entries(tableSchema)) {
    noTypeSchema.defineProp(propId, propSchema.name, null);
    // 找出关联属性的所有schema
    if (propSchema.type === CollectionSchemaType.RELATION && propSchema.collectionId) {
      const schema = getState().blocks[propSchema.collectionId]?.data.schema;
      if (schema) {
        for (const [propId, propSchema] of Object.entries(schema)) {
          relationPageNoTypeSchema.defineProp(propId, propSchema.name, null);
          relationSchema[propId] = propSchema;
        }
      }
    }
  }

  const fromServer = (serverFormula: formula.ServerFormula) => {
    try {
      const result = formula.Formula.fromServer(serverFormula, noTypeSchema, [
        relationPageNoTypeSchema,
      ]);
      return result;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(err);
      return '';
    }
  };

  const computeAvailablePropsForFormula = (
    formulaPropId: string,
    tableSchema: Record<string, CollectionSchema>
  ) => {
    const result = new Set<string>();
    for (const [propId] of Object.entries(tableSchema)) {
      if (!checkPropRecur(collId, formulaPropId, collId, propId, resolver)) {
        result.add(propId);
      }
    }
    return result;
  };

  const formulaExprs = new Map<string, formula.Expr | null>();

  const buildSchema = (propIds: Set<string>, tableSchema: Record<string, CollectionSchema>) => {
    const schema = new formula.Schema();
    for (const propId of propIds) {
      const propSchema = tableSchema[propId];
      if (propSchema == null) continue;

      if (propSchema.type === CollectionSchemaType.FORMULA) {
        const expr = formulaExprs.get(propId);
        schema.defineProp(
          propId,
          propSchema.name,
          expr == null ? formula.ValueTool.textType : formula.ExprTool.typeOf(expr)
        );
      } else if (propSchema.type === CollectionSchemaType.ROLLUP) {
        if (isShowOriginalValue(propSchema.aggregation)) {
          schema.defineProp(propId, propSchema.name, formula.ValueTool.textType);
        } else if (isDateAggregation(propSchema.aggregation)) {
          if (propSchema.aggregation === AggregationAction.DATE_RANGE) {
            schema.defineProp(propId, propSchema.name, formula.ValueTool.textType);
          } else {
            schema.defineProp(propId, propSchema.name, formula.ValueTool.dateType);
          }
        } else {
          schema.defineProp(propId, propSchema.name, formula.ValueTool.numberType);
        }
      } else if (propSchema.type === CollectionSchemaType.NUMBER) {
        schema.defineProp(propId, propSchema.name, formula.ValueTool.numberType);
      } else if (
        propSchema.type === CollectionSchemaType.DATE ||
        propSchema.type === CollectionSchemaType.CREATED_AT ||
        propSchema.type === CollectionSchemaType.UPDATED_AT
      ) {
        schema.defineProp(propId, propSchema.name, formula.ValueTool.dateType);
      } else if (propSchema.type === CollectionSchemaType.CHECKBOX) {
        schema.defineProp(propId, propSchema.name, formula.ValueTool.booleanType);
      } else if (propSchema.type === CollectionSchemaType.PERSON) {
        schema.defineProp(
          propId,
          propSchema.name,
          formula.ValueTool.listType(formula.ValueTool.userType)
        );
      } else if (propSchema.type === CollectionSchemaType.RELATION) {
        schema.defineProp(
          propId,
          propSchema.name,
          formula.ValueTool.listType(formula.ValueTool.relationPage)
        );
      } else if (propSchema.type === CollectionSchemaType.MULTI_SELECT) {
        schema.defineProp(
          propId,
          propSchema.name,
          formula.ValueTool.listType(formula.ValueTool.textType)
        );
      } else {
        schema.defineProp(propId, propSchema.name, formula.ValueTool.textType);
      }
    }
    return schema;
  };
  const processFormula = (propId: string, tableSchema: Record<string, CollectionSchema>) => {
    const propSchema = tableSchema[propId];
    if (
      propSchema == null ||
      propSchema.type !== CollectionSchemaType.FORMULA ||
      formulaExprs.has(propId)
    ) {
      return;
    }

    const fail = () => {
      formulaExprs.set(propId, null);
    };
    if (propSchema.formula == null) return fail();
    let serverFormula = propSchema.formula as any as formula.ServerFormula;
    // 如果不是2.0版本的就需要升级一下
    serverFormula = tryNewFormula(serverFormula, collId);
    const availablePropIds = computeAvailablePropsForFormula(propId, tableSchema);

    const refPropIds = Object.keys(serverFormula.refProps);
    const relationAvailablePropIds = computeAvailablePropsForFormula(propId, relationSchema);
    if (!refPropIds.every((it) => availablePropIds.has(it) || relationAvailablePropIds.has(it))) {
      return fail();
    }
    // 关联的
    // 优先处理依赖
    for (const propId of refPropIds) processFormula(propId, tableSchema);
    const formulaRelationSchema = buildSchema(relationAvailablePropIds, relationSchema);
    const schema = buildSchema(availablePropIds, tableSchema);
    const formulaText = fromServer(serverFormula);
    let result;
    try {
      result = formula.Formula.parse(formulaText, schema, [formulaRelationSchema]);
    } catch (err) {
      console.log('err: ', err);
      return;
    }

    if (formula.ResultTool.isError(result)) return fail();
    const expr = formula.ResultTool.getOk(result);
    formulaExprs.set(propId, expr);
  };
  for (const propId of Object.keys(tableSchema)) {
    processFormula(propId, tableSchema);
  }

  const getBoxedValue = (recordId: string, propId: string) => {
    if (!formulaExprs.has(propId)) {
      return evalCtx.readProp(recordId, propId);
    }
    const expr = formulaExprs.get(propId);
    if (expr == null) return null;
    return formula.Formula.eval(expr, evalCtx, recordId);
  };
  const getValue = (recordId: string, propId: string) => {
    const value = getBoxedValue(recordId, propId);
    if (value == null) return null;
    const type = formula.ValueTool.typeOf(value);
    if (type === formula.ValueTool.numberType) {
      return formula.ValueTool.asNumber(value);
    } else if (type === formula.ValueTool.booleanType) {
      return formula.ValueTool.asBoolean(value);
    } else if (type === formula.ValueTool.dateType) {
      return formula.ValueTool.asDate(value);
    } else if (type === formula.ValueTool.textType) {
      return formula.ValueTool.asText(value);
    } else if (formula.ValueTool.isListType(type)) {
      return formula.ValueTool.asList(value);
    } else if (type === formula.ValueTool.dateRangeType) {
      return formula.ValueTool.asDateRange(value);
    } else if (type === formula.ValueTool.selectType) {
      return formula.ValueTool.asSelect(value);
    } else if (type === formula.ValueTool.fileType) {
      return formula.ValueTool.asFile(value);
    } else if (type === formula.ValueTool.userType) {
      return formula.ValueTool.asUser(value);
    } else if (type === formula.ValueTool.relationPage) {
      return formula.ValueTool.asPage(value);
    }
    return null;
  };
  const getType = (propId: string): formula.ValueType | null => {
    const expr = formulaExprs.get(propId);
    return expr == null ? null : formula.ExprTool.typeOf(expr);
  };
  const getRelationSchema = () => {
    return relationSchema;
  };
  return {
    fromServer,
    toServer(formulaText: string) {
      return formula.Formula.toServer(formulaText, noTypeSchema, [relationPageNoTypeSchema]);
    },
    getRelationSchema,
    getType,
    getTypeAsCollectionSchemaType(propId: string): CollectionSchemaType {
      const type = getType(propId);
      if (type === formula.ValueTool.numberType) return CollectionSchemaType.NUMBER;
      if (type === formula.ValueTool.booleanType) return CollectionSchemaType.CHECKBOX;
      if (
        type === formula.ValueTool.dateType ||
        (type === formula.ValueTool.dateType) === formula.ValueTool.dateRangeType
      ) {
        return CollectionSchemaType.DATE;
      }
      return CollectionSchemaType.TEXT;
    },
    getBoxedValue,
    getValue,
    getValueAsString(recordId: string, propId: string): string | undefined {
      const segments = this.getValueAsSegments(recordId, propId);
      return segmentsToText(segments);
    },
    getValueAsSegments(recordId: string, propId: string): SegmentDTO[] | undefined {
      const value = getBoxedValue(recordId, propId);
      if (value == null) return undefined;
      const type = formula.ValueTool.typeOf(value);
      if (type === formula.ValueTool.numberType) {
        return [
          {
            type: TextType.TEXT,
            text: `${formula.ValueTool.asNumber(value)}`,
            enhancer: {},
          },
        ];
      } else if (type === formula.ValueTool.textType) {
        return [
          {
            type: TextType.TEXT,
            text: formula.ValueTool.asString(value),
            enhancer: {},
          },
        ];
      } else if (type === formula.ValueTool.booleanType) {
        return formula.ValueTool.asBoolean(value)
          ? [
              {
                type: TextType.TEXT,
                text: 'YES',
                enhancer: {},
              },
            ]
          : undefined;
      } else if (type === formula.ValueTool.dateType) {
        return [buildDateSegment({ from: formula.ValueTool.asDate(value) })];
      }
      return textToSegments(formula.ValueTool.toString(value));
    },
    getAvailablePropIds(propId: string) {
      return computeAvailablePropsForFormula(propId, tableSchema);
    },
    render(recordId: string, propId: string) {
      const expr = formulaExprs.get(propId);
      const value = expr == null ? null : formula.Formula.eval(expr, evalCtx, recordId);

      return value ? formula.ValueTool.toString(value) : null;
    },
    validate(
      text: string,
      collectionId: string,
      recordId: string,
      propId
    ): FormulaError | FormulaPreview {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const availablePropIds = computeAvailablePropsForFormula(propId, tableSchema);

      const schema = buildSchema(availablePropIds, tableSchema);
      const relationAvailablePropIds = computeAvailablePropsForFormula(propId, relationSchema);
      const formulaRelationSchema = buildSchema(relationAvailablePropIds, relationSchema);
      const result = formula.Formula.parse(text, schema, [formulaRelationSchema]);
      if (formula.ResultTool.isError(result)) {
        const error = formula.ResultTool.getError(result);
        return {
          type: 'error',
          pos: formula.ErrorTool.getPosition(error),
          message: translateFormulaError(formula.ErrorTool.toString(error)),
        };
      }
      const expr = formula.ResultTool.getOk(result);
      const value = formula.Formula.eval(expr, evalCtx, recordId);
      if (!value) {
        return {
          type: 'preview',
          preview: '',
        };
      }
      const type = formula.ValueTool.typeOf(value);
      let preview: string | React.ReactNode = '';
      if (type === formula.ValueTool.numberType) {
        preview = formatFloat(formula.ValueTool.asNumber(value));
      } else if (type === formula.ValueTool.textType) {
        preview = <FormulaValue value={value} collectionId={collectionId} propertyId={propId} />;
      } else if (type === formula.ValueTool.dateType) {
        const v = formula.ValueTool.asDate(value);
        if (v instanceof Date) {
          const dateFormat = generateFormatByDate(v);
          preview = dayjs(v)
            .locale(isBuildIn() ? 'en' : 'zh-cn')
            .format(dateFormat);
        } else {
          const v2 = v as formula.DateRangeValue;
          const dateFormat = generateFormatByDate(v2.start);
          preview = `${dayjs(v2.start)
            .locale(isBuildIn() ? 'en' : 'zh-cn')
            .format(dateFormat)} → ${dayjs(v2.end)
            .locale(isBuildIn() ? 'en' : 'zh-cn')
            .format(dateFormat)}`;
        }
      } else if (type === formula.ValueTool.userType) {
        preview = <FormulaValue value={value} collectionId={collectionId} propertyId={propId} />;
      } else if (type === formula.ValueTool.booleanType) {
        preview = <FormulaValue value={value} collectionId={collectionId} propertyId={propId} />;
      } else if (type === formula.ValueTool.relationPage) {
        preview = <FormulaValue value={value} collectionId={collectionId} propertyId={propId} />;
      } else if (formula.ValueTool.isListType(type)) {
        const list = formula.ValueTool.asList(value);
        const elementType = formula.ValueTool.getListElementType(type);
        if (elementType === formula.ValueTool.textType) {
          preview = <FormulaValue value={value} collectionId={collectionId} propertyId={propId} />;
        } else if (elementType === formula.ValueTool.userType) {
          preview = <FormulaValue value={value} collectionId={collectionId} propertyId={propId} />;
        } else if (elementType === formula.ValueTool.relationPage) {
          preview = <FormulaValue value={value} collectionId={collectionId} propertyId={propId} />;
        } else if (elementType === formula.ValueTool.booleanType) {
          preview = <FormulaValue value={value} collectionId={collectionId} propertyId={propId} />;
        } else if (elementType === formula.ValueTool.selectType) {
          preview = list.elements
            .map((v) => {
              return (v as formula.Select).name;
            })
            .join(',');
        } else if (elementType === formula.ValueTool.fileType) {
          preview = list.elements
            .map((v) => {
              return (v as formula.File).name;
            })
            .join(',');
        } else if (elementType === formula.ValueTool.dateType) {
          preview = list.elements
            .map((v) => {
              if (!v) return '';
              if (v instanceof Date) {
                const dateFormat = generateFormatByDate(v);
                return dayjs(v)
                  .locale(isBuildIn() ? 'en' : 'zh-cn')
                  .format(dateFormat);
              }
              const v2 = v as formula.DateRangeValue;
              const dateFormat = generateFormatByDate(v2.start);
              return `${dayjs(v2.start)
                .locale(isBuildIn() ? 'en' : 'zh-cn')
                .format(dateFormat)} → ${dayjs(v2.end)
                .locale(isBuildIn() ? 'en' : 'zh-cn')
                .format(dateFormat)}`;
            })
            .join(',');
        } else if (formula.ValueTool.isListType(elementType)) {
          preview = list.elements
            .map((v) => {
              return (v as formula.ListValue).elements
                .map((v) => {
                  const value = formula.ValueTool.fromDynamic(v);
                  return formula.ValueTool.toString(value);
                })
                .join(',');
            })
            .filter((v) => v)
            .join(',');
        } else {
          // TString
          preview = list.elements
            .map((v) => {
              return v;
            })
            .join(',');
        }
      } else {
        preview = formula.ValueTool.toString(value);
      }
      return {
        type: 'preview',
        preview,
      };
    },
  } as FormulaTool;
};

// 使用 Symbol 来避免缓存巨大无比的 state
const FORMULA_TOOLS_OF_STATE = Symbol('Formula Tools');

const getFormulaToolNoMemo = (collId: string, state: RootState) => {
  const schema = state.blocks[collId]?.data.schema ?? {};
  const evalCtx = new MyEvalContext(() => state, collId);
  const resolver: BiSchemaResolver = {
    findSchema(collId, propId) {
      return state.blocks[collId]?.data.schema?.[propId];
    },
  };
  return createFormulaTool(collId, schema, resolver, evalCtx);
};

export const getFormulaTool = (collId: string, state = getAppState()) => {
  let formulaTools = (state as any)[FORMULA_TOOLS_OF_STATE] as Map<string, FormulaTool> | undefined;
  if (formulaTools == null) {
    formulaTools = new Map();
    (state as any)[FORMULA_TOOLS_OF_STATE] = formulaTools;
  }
  let formulaTool = formulaTools.get(collId);
  if (formulaTool == null) {
    formulaTool = getFormulaToolNoMemo(collId, state);
    formulaTools.set(collId, formulaTool);
  }
  return formulaTool;
};
